import { useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTeamMemberProfile, useTeamMemberProjects, useTeamMemberActivity } from "@/hooks/useTeamMemberDetail";
import { useUpdateCompanyUser } from "@/hooks/useCompanyUsers";
import { usePermissions } from "@/hooks/usePermissions";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/controller/StatusBadge";
import { ArrowLeft, Camera, Mail, Phone, Briefcase, Calendar, Shield } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

const TeamMemberDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { isAdmin } = usePermissions();
  const { data: profile, isLoading } = useTeamMemberProfile(id);
  const { data: projects } = useTeamMemberProjects(id);
  const { approvals } = useTeamMemberActivity(id);
  const updateUser = useUpdateCompanyUser();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  // Editable fields
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editNotes, setEditNotes] = useState("");

  const startEdit = () => {
    setEditTitle(profile?.title || "");
    setEditPhone(profile?.phone || "");
    setEditNotes(profile?.notes || "");
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!profile) return;
    try {
      await updateUser.mutateAsync({
        id: profile.id,
        title: editTitle || null,
        phone: editPhone || null,
        notes: editNotes || null,
      });
      toast.success("Profile updated");
      setEditing(false);
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !profile) return;
    setUploading(true);
    try {
      const ext = file.name.split(".").pop();
      const path = `${profile.user_id}/${Date.now()}.${ext}`;

      // Delete old photo if exists
      if (profile.profile_photo_path) {
        await supabase.storage.from("user-profile-photos").remove([profile.profile_photo_path]);
      }

      const { error: uploadErr } = await supabase.storage.from("user-profile-photos").upload(path, file);
      if (uploadErr) throw uploadErr;

      const { data: urlData } = supabase.storage.from("user-profile-photos").getPublicUrl(path);

      await updateUser.mutateAsync({
        id: profile.id,
        profile_photo_url: urlData.publicUrl,
        profile_photo_path: path,
      });
      qc.invalidateQueries({ queryKey: ["team_member_profile", id] });
      toast.success("Photo updated");
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const initials = profile
    ? `${(profile.first_name || "")[0] || ""}${(profile.last_name || "")[0] || ""}`.toUpperCase() || "?"
    : "?";

  if (isLoading) return <p className="text-muted-foreground p-4">Loading…</p>;
  if (!profile) return <p className="text-muted-foreground p-4">Team member not found.</p>;

  return (
    <div className="space-y-6">
      {/* Back */}
      <Button variant="ghost" size="sm" className="-ml-2" onClick={() => navigate("/controller/team")}>
        <ArrowLeft className="mr-1 h-4 w-4" /> Back to Team
      </Button>

      {/* Profile Header */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-start gap-4">
            {/* Avatar with upload */}
            <div className="relative group">
              <Avatar className="h-20 w-20">
                {profile.profile_photo_url && <AvatarImage src={profile.profile_photo_url} />}
                <AvatarFallback className="text-xl">{initials}</AvatarFallback>
              </Avatar>
              {isAdmin && (
                <>
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Camera className="h-5 w-5 text-white" />
                  </button>
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
                </>
              )}
            </div>
            <div className="flex-1 space-y-1">
              <h1 className="text-2xl font-bold">{profile.first_name} {profile.last_name}</h1>
              {profile.title && <p className="text-muted-foreground">{profile.title}</p>}
              <div className="flex flex-wrap items-center gap-3 text-sm">
                {profile.roles?.name && (
                  <Badge variant="outline" style={profile.roles.color ? { borderColor: profile.roles.color, color: profile.roles.color } : {}}>
                    <Shield className="h-3 w-3 mr-1" />{profile.roles.name}
                  </Badge>
                )}
                <Badge variant={profile.status === "active" ? "default" : "secondary"}>{profile.status}</Badge>
              </div>
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground pt-1">
                {profile.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{profile.email}</span>}
                {profile.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{profile.phone}</span>}
              </div>
              <div className="flex flex-wrap gap-4 text-xs text-muted-foreground pt-2">
                {profile.created_at && (
                  <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />Joined {format(new Date(profile.created_at), "MMM d, yyyy")}</span>
                )}
                {profile.last_login && (
                  <span>Last login: {format(new Date(profile.last_login), "MMM d, yyyy h:mm a")}</span>
                )}
              </div>
            </div>
            {isAdmin && !editing && (
              <Button variant="outline" size="sm" onClick={startEdit}>Edit</Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="projects">
        <TabsList>
          <TabsTrigger value="projects">Assigned Projects</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
          <TabsTrigger value="info">Info</TabsTrigger>
        </TabsList>

        {/* Projects */}
        <TabsContent value="projects" className="space-y-4">
          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {projects?.map((pa: any) => (
              <Card key={pa.id} className="cursor-pointer active:bg-muted/50" onClick={() => navigate(`/controller/projects/${pa.projects?.id}`)}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{pa.projects?.name || "Unknown"}</p>
                    <p className="text-xs text-muted-foreground">{pa.projects?.project_number || "—"}</p>
                  </div>
                  <StatusBadge status={pa.projects?.status} />
                </CardContent>
              </Card>
            ))}
            {!projects?.length && <p className="text-center text-muted-foreground py-8">Not assigned to any projects.</p>}
          </div>
          {/* Desktop table */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Project #</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projects?.map((pa: any) => (
                  <TableRow key={pa.id} className="cursor-pointer hover:bg-muted/50" onClick={() => navigate(`/controller/projects/${pa.projects?.id}`)}>
                    <TableCell className="font-mono text-sm">{pa.projects?.project_number || "—"}</TableCell>
                    <TableCell className="font-medium">{pa.projects?.name || "Unknown"}</TableCell>
                    <TableCell className="text-sm capitalize">{pa.projects?.project_type || "—"}</TableCell>
                    <TableCell><StatusBadge status={pa.projects?.status} /></TableCell>
                  </TableRow>
                ))}
                {!projects?.length && (
                  <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">Not assigned to any projects.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Activity */}
        <TabsContent value="activity" className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Recent Approval Requests</CardTitle></CardHeader>
            <CardContent>
              {approvals.data?.length ? (
                <div className="space-y-2">
                  {approvals.data.map((a: any) => (
                    <div key={a.id} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{a.title}</p>
                        <p className="text-xs text-muted-foreground capitalize">{a.request_type.replace(/_/g, " ")}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <StatusBadge status={a.status} />
                        <span className="text-xs text-muted-foreground">{format(new Date(a.created_at), "MMM d")}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No recent approval requests.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Info */}
        <TabsContent value="info" className="space-y-4">
          {editing ? (
            <Card>
              <CardContent className="p-6 space-y-4">
                <div>
                  <Label>Title</Label>
                  <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={editPhone} onChange={(e) => setEditPhone(e.target.value)} />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea value={editNotes} onChange={(e) => setEditNotes(e.target.value)} rows={3} />
                </div>
                <div className="flex gap-2">
                  <Button onClick={saveEdit} disabled={updateUser.isPending}>Save</Button>
                  <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-6 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><p className="text-xs text-muted-foreground">First Name</p><p className="font-medium">{profile.first_name || "—"}</p></div>
                  <div><p className="text-xs text-muted-foreground">Last Name</p><p className="font-medium">{profile.last_name || "—"}</p></div>
                  <div><p className="text-xs text-muted-foreground">Email</p><p className="font-medium">{profile.email || "—"}</p></div>
                  <div><p className="text-xs text-muted-foreground">Phone</p><p className="font-medium">{profile.phone || "—"}</p></div>
                  <div><p className="text-xs text-muted-foreground">Title</p><p className="font-medium">{profile.title || "—"}</p></div>
                  <div><p className="text-xs text-muted-foreground">Status</p><p className="font-medium capitalize">{profile.status}</p></div>
                </div>
                {profile.notes && (
                  <div className="pt-2 border-t">
                    <p className="text-xs text-muted-foreground mb-1">Notes</p>
                    <p className="text-sm">{profile.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TeamMemberDetail;
